"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumProjectFileUtil = exports.AltiumProjectFileDesignDefaults = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const altiumFileTypes_1 = require("../../altiumFileTypes");
const altiumValidation_1 = require("../../altiumValidation");
const altiumProjectFileTypes_1 = require("./altiumProjectFileTypes");
exports.AltiumProjectFileDesignDefaults = Object.freeze({
    Design: Object.freeze({
        ChannelDesignatorFormatString: '$Component_$RoomName',
        ChannelRoomLevelSeperator: '_',
        AllowPortNetNames: false,
        AllowSheetEntryNetNames: false,
        AppendSheetNumberToLocalNets: false,
        AutoSheetNumbering: false,
        PowerPortNamesTakePriority: altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.No,
        NameNetsHierarchically: altiumProjectFileTypes_1.NameNetsHierarchicallyMode.No,
        HierarchyMode: altiumProjectFileTypes_1.HierarchyMode.Automatic,
        ChannelRoomNamingStyle: 0,
        AnnotateSortOrder: altiumProjectFileTypes_1.AnnotateSortOrder.AcrossThenUp,
        AnnotateProcessLocation: altiumProjectFileTypes_1.AnnotateProcessLocation.Designator,
    }),
});
const EMPTY_STRING_MAP = new Map();
class AltiumProjectFileUtil {
    static { this.UNAPPLIED_VARIANT_COLUMN_PREFIX = 'Variant: '; }
    static { this.NOT_FITTED_LABEL = 'Not Fitted'; }
    static { this.VARIATION_KIND_LABELS = {
        [altiumProjectFileTypes_1.AltiumVariationKind.Fitted]: 'Fitted',
        [altiumProjectFileTypes_1.AltiumVariationKind.NotFitted]: AltiumProjectFileUtil.NOT_FITTED_LABEL,
        [altiumProjectFileTypes_1.AltiumVariationKind.AlternatePart]: 'Alternate Part',
    }; }
    constructor(projectFile, prjpcbStructureFile) {
        this.projectFile = projectFile;
        this.prjpcbStructureFile = prjpcbStructureFile;
        this.paramVariationIndexes = new Map();
        this.projectDocumentPathResolutions = new Map();
    }
    get projectFileName() {
        return this.projectFile[altiumValidation_1.AltiumProjectMetadataKey.FileName];
    }
    getDifferentialPairSuffixes() {
        const declaredSuffixes = this.projectFile.DiffPairSuffix?.flatMap((suffix) => [suffix.Positive, suffix.Negative]) ?? [];
        const suffixes = ['_P', '_N', ...declaredSuffixes].filter((suffix) => suffix.length > 0 && suffix.startsWith('_'));
        const uniqueSuffixes = new Map();
        for (const suffix of suffixes) {
            const key = suffix.toLowerCase();
            if (!uniqueSuffixes.has(key)) {
                uniqueSuffixes.set(key, suffix);
            }
        }
        return Array.from(uniqueSuffixes.values()).sort((a, b) => b.length - a.length);
    }
    get sheetCount() {
        return this.projectFile.Document?.filter((d) => d.DocumentPath.toLowerCase().endsWith('.schdoc')).length ?? 0;
    }
    get hasPrjpcbStructureFile() {
        return this.prjpcbStructureFile !== undefined;
    }
    get listsNoSchematics() {
        return !this.hasPrjpcbStructureFile && this.sheetCount === 0;
    }
    checkHierarchyModeRequiresProjectStructure(hierarchyMode) {
        return hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || hierarchyMode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical;
    }
    getDesignConfiguration(name) {
        if (!this.projectFile?.Design || !(name in this.projectFile.Design)) {
            return exports.AltiumProjectFileDesignDefaults.Design[name];
        }
        return this.projectFile?.Design?.[name];
    }
    getTopLevelDocuments(type) {
        const filterType = (f) => (type ? f.toLowerCase().endsWith(type.toLowerCase()) : true);
        const path = this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName] ?? this.projectFileName;
        const isUsingPrjStructure = path === this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName];
        if (this.prjpcbStructureFile?.topLevelDocuments) {
            return this.makeFilesRelative(this.prjpcbStructureFile.topLevelDocuments.filter((d) => filterType(d.FILENAME)).map((d) => d.FILENAME), path, isUsingPrjStructure);
        }
        if (this.projectFile.Document?.length) {
            const docs = this.projectFile.Document?.sort((a, b) => (a.AnnotateOrder ?? 0) - (b.AnnotateOrder ?? 0))
                .filter((d) => filterType(d.DocumentPath))
                .map((d) => d.DocumentPath);
            return this.makeFilesRelative(docs, path, isUsingPrjStructure);
        }
        if (this.prjpcbStructureFile?.sheetSymbols.length === 1) {
            return this.makeFilesRelative([this.prjpcbStructureFile.sheetSymbols[0].FILENAME], path, isUsingPrjStructure).filter(filterType);
        }
        throw new Error('No available settings to read top level document');
    }
    getNoMainPathDocuments(type) {
        const filterType = (f) => (type ? f.toLowerCase().endsWith(type.toLowerCase()) : true);
        const path = this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName] ?? this.projectFileName;
        const isUsingPrjStructure = path === this.prjpcbStructureFile?.[altiumValidation_1.AltiumProjectMetadataKey.FileName];
        const noMainPathDocuments = this.prjpcbStructureFile?.noMainPathDocuments ?? [];
        return this.makeFilesRelative(noMainPathDocuments.filter((d) => filterType(d.FILENAME)).map((d) => d.FILENAME), path, isUsingPrjStructure);
    }
    getFlatProjectDocuments(type) {
        const seen = new Set();
        const documents = [...this.getTopLevelDocuments(type), ...this.getNoMainPathDocuments(type)];
        return documents.filter((document) => {
            const normalized = document.replace(/\\/g, '/').toLowerCase();
            if (seen.has(normalized)) {
                return false;
            }
            seen.add(normalized);
            return true;
        });
    }
    getPartVariationForUniqueId(uniqueId, variant = this.projectFile.Design?.CurrentVariant) {
        const projectVariant = this.projectFile.ProjectVariant?.find((v) => v.Description === variant);
        if (!projectVariant) {
            if (!variant) {
                return undefined;
            }
            throw new Error(`Could not find project variant '${variant}'`);
        }
        return projectVariant.Variations.find((v) => v.UniqueId === uniqueId);
    }
    getUnappliedVariantColumnsForUniqueId(uniqueId) {
        if (this.projectFile.Design?.CurrentVariant) {
            return EMPTY_STRING_MAP;
        }
        this.unappliedVariantColumns ??= this.buildUnappliedVariantColumns();
        return this.unappliedVariantColumns.get(uniqueId) ?? EMPTY_STRING_MAP;
    }
    getParamVariationsForVariation(variation, variant = this.projectFile.Design?.CurrentVariant) {
        if (variant === undefined) {
            return EMPTY_STRING_MAP;
        }
        let index = this.paramVariationIndexes.get(variant);
        if (!index) {
            index = this.buildParamVariationIndex(variant);
            this.paramVariationIndexes.set(variant, index);
        }
        return index.get(variation.Designator) ?? EMPTY_STRING_MAP;
    }
    buildUnappliedVariantColumns() {
        const index = new Map();
        for (const projectVariant of this.projectFile.ProjectVariant ?? []) {
            const column = `${AltiumProjectFileUtil.UNAPPLIED_VARIANT_COLUMN_PREFIX}${projectVariant.Description}`;
            for (const variation of projectVariant.Variations) {
                let columns = index.get(variation.UniqueId);
                if (!columns) {
                    columns = new Map();
                    index.set(variation.UniqueId, columns);
                }
                columns.set(column, AltiumProjectFileUtil.VARIATION_KIND_LABELS[variation.Kind] ?? `Kind ${variation.Kind}`);
            }
        }
        return index;
    }
    buildParamVariationIndex(variant) {
        const projectVariant = this.projectFile.ProjectVariant?.find((v) => v.Description === variant);
        const index = new Map();
        for (const paramVariation of projectVariant?.ParamVariations ?? []) {
            if (!paramVariation.ParameterName) {
                continue;
            }
            let overrides = index.get(paramVariation.Designator);
            if (!overrides) {
                overrides = new Map();
                index.set(paramVariation.Designator, overrides);
            }
            overrides.set(paramVariation.ParameterName, paramVariation.VariantValue);
        }
        return index;
    }
    makeFilesRelative(files, path, searchFilesInProjectFile) {
        if (searchFilesInProjectFile) {
            return files.map((fileName) => {
                const resolvedPath = this.resolveProjectDocumentPath(fileName).documentPath;
                if (resolvedPath !== undefined) {
                    return resolvedPath.replace(/\\/g, '/');
                }
                const normalizedFileName = AltiumProjectFileUtil.normalizeDocumentPath(fileName);
                const matchingDocuments = AltiumProjectFileUtil.uniqueNormalizedDocuments((this.projectFile.Document ?? []).filter((document) => {
                    const documentPath = AltiumProjectFileUtil.normalizeDocumentPath(document.DocumentPath);
                    return documentPath === normalizedFileName || documentPath.endsWith(`/${normalizedFileName}`);
                }));
                return (matchingDocuments.length === 1 ? matchingDocuments[0].DocumentPath : fileName).replace(/\\/g, '/');
            });
        }
        if (path === undefined) {
            return files;
        }
        const resolvedPath = sigma_engine_shared_1.Files.dirnamePath(sigma_engine_shared_1.Files.resolvePath(path));
        return files.map((f) => sigma_engine_shared_1.Files.relativePath(resolvedPath, sigma_engine_shared_1.Files.resolvePath(f.replace(/\\/g, '/'))));
    }
    getDesignConfigurationBoolean(name) {
        return this.getDesignConfiguration(name) == 1;
    }
    getAltiumDocumentConfiguration(documentPath) {
        const resolution = this.resolveProjectDocumentPath(documentPath);
        return this.projectFile?.Document?.find((document) => document.DocumentPath === resolution.documentPath);
    }
    resolveProjectDocumentPath(fileKey) {
        const normalizedFileKey = AltiumProjectFileUtil.normalizeDocumentPath(fileKey);
        const cached = this.projectDocumentPathResolutions.get(normalizedFileKey);
        if (cached) {
            return cached;
        }
        const keyIsSpecial = AltiumProjectFileUtil.isSpecialDocumentPath(fileKey, normalizedFileKey);
        const documents = this.projectFile.Document ?? [];
        const exactMatches = AltiumProjectFileUtil.uniqueNormalizedDocuments(documents.filter((document) => AltiumProjectFileUtil.normalizeDocumentPath(document.DocumentPath) === normalizedFileKey));
        const suffixMatches = exactMatches.length > 0 || keyIsSpecial ?
            []
            : documents.filter((document) => {
                const listedPath = AltiumProjectFileUtil.normalizeDocumentPath(document.DocumentPath);
                return (!AltiumProjectFileUtil.isSpecialDocumentPath(document.DocumentPath, listedPath) &&
                    normalizedFileKey.endsWith(`/${listedPath}`));
            });
        const matches = exactMatches.length > 0 ?
            exactMatches
            : AltiumProjectFileUtil.mostSpecificPaths(AltiumProjectFileUtil.uniqueNormalizedDocuments(suffixMatches));
        const document = matches[0];
        if (!document) {
            const resolution = Object.freeze({});
            this.projectDocumentPathResolutions.set(normalizedFileKey, resolution);
            return resolution;
        }
        const resolution = Object.freeze({ documentPath: document.DocumentPath });
        this.projectDocumentPathResolutions.set(normalizedFileKey, resolution);
        return resolution;
    }
    getAnnotationDocumentPaths() {
        return this.getAnnotationDocumentSelection().paths;
    }
    getAnnotationDocumentPathDiagnostics() {
        return this.getAnnotationDocumentSelection().diagnostics;
    }
    getAnnotationDocumentSelection() {
        const annotationDocuments = (this.projectFile.Document ?? []).filter((document) => document.DocumentPath.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase()));
        const diagnostics = [];
        const documentsByIndex = new Map();
        for (const document of annotationDocuments) {
            if (!Number.isSafeInteger(document._DocumentIndex) || document._DocumentIndex < 0) {
                diagnostics.push(`annotation-manager-index-ambiguous: ${document.DocumentPath} has malformed _DocumentIndex ${String(document._DocumentIndex)}`);
                continue;
            }
            const indexedDocuments = documentsByIndex.get(document._DocumentIndex) ?? [];
            indexedDocuments.push(document);
            documentsByIndex.set(document._DocumentIndex, indexedDocuments);
        }
        for (const [index, documents] of documentsByIndex) {
            if (documents.length > 1) {
                diagnostics.push(`annotation-manager-index-ambiguous: _DocumentIndex ${index} is shared by ${documents.map((document) => document.DocumentPath).join(', ')}`);
            }
        }
        if (diagnostics.length > 0) {
            return { paths: [], diagnostics };
        }
        return {
            paths: AltiumProjectFileUtil.uniqueNormalizedDocuments([...annotationDocuments].sort((left, right) => left._DocumentIndex - right._DocumentIndex)).map((document) => document.DocumentPath),
            diagnostics: [],
        };
    }
    getDocumentUniqueId(documentPath) {
        return this.getAltiumDocumentConfiguration(documentPath)?.DocumentUniqueId;
    }
    getAltiumSchematicAnnotationOrder(fileName) {
        const order = this.getAltiumDocumentConfiguration(fileName)?.AnnotateOrder;
        if (order === undefined) {
            throw new Error(`Could not find annotation order for ${fileName}`);
        }
        if (typeof order === 'string') {
            return parseInt(order, 10);
        }
        return order;
    }
    getProjectParameter(key) {
        const normalizedKey = key.toLowerCase();
        return this.projectFile?.Parameter?.find((p) => typeof p.Name === 'string' && p.Name.toLowerCase() === normalizedKey)?.Value;
    }
    getAltiumSchematicSheetNumber(fileName) {
        const resolution = this.resolveProjectDocumentPath(fileName);
        const projectDocumentPath = resolution.documentPath ?? fileName;
        const sheetNumber = (this.prjpcbStructureFile?.topLevelDocuments.find((d) => this.matchesStructureFileName(d.FILENAME, projectDocumentPath)) ??
            this.prjpcbStructureFile?.sheetSymbols.find((d) => this.matchesStructureFileName(d.FILENAME, projectDocumentPath)) ??
            this.prjpcbStructureFile?.noMainPathDocuments.find((d) => this.matchesStructureFileName(d.FILENAME, projectDocumentPath)))?.SHEETNUMBER;
        if (sheetNumber === undefined) {
            throw new Error(`Could not find sheet number for ${fileName}`);
        }
        if (typeof sheetNumber === 'string') {
            return parseInt(sheetNumber, 10);
        }
        return sheetNumber;
    }
    getUnambiguousAltiumSchematicSheetNumber(fileName) {
        const resolution = this.resolveProjectDocumentPath(fileName);
        const projectDocumentPath = resolution.documentPath ?? fileName;
        const records = [
            ...(this.prjpcbStructureFile?.topLevelDocuments ?? []),
            ...(this.prjpcbStructureFile?.sheetSymbols ?? []),
            ...(this.prjpcbStructureFile?.noMainPathDocuments ?? []),
        ].filter((record) => this.matchesStructureFileName(record.FILENAME, projectDocumentPath));
        if (records.length === 0) {
            throw new Error(`Could not find sheet number for ${fileName}`);
        }
        if (records.length > 1) {
            throw new Error(`Multiple structural sheet records match ${fileName}`);
        }
        const sheetNumber = records[0]?.SHEETNUMBER;
        if (sheetNumber === undefined) {
            throw new Error(`Could not find sheet number for ${fileName}`);
        }
        const parsedSheetNumber = Number(sheetNumber);
        if (!Number.isInteger(parsedSheetNumber) || parsedSheetNumber < 1) {
            throw new Error(`Invalid sheet number for ${fileName}`);
        }
        return parsedSheetNumber;
    }
    matchesStructureFileName(structureFileName, fileName) {
        const normalizedStructureFileName = AltiumProjectFileUtil.normalizeDocumentPath(structureFileName);
        const normalizedFileName = AltiumProjectFileUtil.normalizeDocumentPath(fileName);
        if (normalizedStructureFileName === normalizedFileName) {
            return true;
        }
        return this.resolveProjectDocumentPath(structureFileName).documentPath === fileName;
    }
    static normalizeDocumentPath(path) {
        return path
            .replace(/\\/g, '/')
            .replace(/(?<!^)\/{2,}/g, '/')
            .replace(/^(\.\/)+/, '');
    }
    static isAbsoluteDocumentPath(path) {
        const slashPath = path.replace(/\\/g, '/');
        return slashPath.startsWith('/') || /^[A-Za-z]:\//.test(slashPath);
    }
    static hasParentSegment(path) {
        return path.split('/').includes('..');
    }
    static isSpecialDocumentPath(rawPath, normalizedPath) {
        return this.isAbsoluteDocumentPath(rawPath) || this.hasParentSegment(normalizedPath);
    }
    static uniqueNormalizedDocuments(documents) {
        const uniqueDocuments = new Map();
        for (const document of documents) {
            const normalizedPath = this.normalizeDocumentPath(document.DocumentPath);
            const existing = uniqueDocuments.get(normalizedPath);
            if (existing === undefined ||
                (existing.DocumentPath !== normalizedPath && document.DocumentPath === normalizedPath)) {
                uniqueDocuments.set(normalizedPath, document);
            }
        }
        return Array.from(uniqueDocuments.values());
    }
    static mostSpecificPaths(documents) {
        const maximumSegments = Math.max(0, ...documents.map((document) => this.normalizeDocumentPath(document.DocumentPath).split('/').length));
        return documents.filter((document) => this.normalizeDocumentPath(document.DocumentPath).split('/').length === maximumSegments);
    }
}
exports.AltiumProjectFileUtil = AltiumProjectFileUtil;
//# sourceMappingURL=altiumProjectFileUtil.js.map